import { ActionBar } from '@/modules/application';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Button,
  Chip,
  Box,
} from '@mui/material';
import { FunctionComponent, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Add, Delete } from '@mui/icons-material';
import { DeviceFormModel } from './DeviceFormModel';
import { Permission } from '@/modules/users/submodules/roles/api/permissions.contracts';
import { useUser } from '@/modules/users/contexts/UserContext';
import { useTranslation } from '@/lib';
import { DeviceType } from '../../api/device/device.contracts';
import { LocationSelect } from '@/modules/locations/components/LocationSelect';

interface DeviceFormDisplayProps {
  form: UseFormReturn<DeviceFormModel>;
  onSubmit: (data: DeviceFormModel) => void;
  isCreate?: boolean;
}

const DeviceFormDisplay: FunctionComponent<DeviceFormDisplayProps> = ({ form, onSubmit, isCreate: _isCreate = false }) => {
  const { formState, handleSubmit, control, watch } = form;
  const { errors, isSubmitting } = formState;
  const hasDeviceEditPermission = useUser().hasPermission(Permission.DEVICES_EDIT);
  const { t } = useTranslation();
  const [newEmail, setNewEmail] = useState('');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3 ">
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" className="pb-2" color="textSecondary">
              {t('details')}
            </Typography>
            <div className="flex w-full flex-col gap-y-2">
              <Controller
                name="deviceType"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <FormControl>
                    <InputLabel id="demo-simple-select-helper-label">{t('type')}</InputLabel>
                    <Select labelId="demo-simple-select-helper-label" id="demo-simple-select-helper" label={t('type')} {...field}>
                      <MenuItem value={DeviceType.MOBILE_SCANNER}>{t('mobile_scanner')}</MenuItem>
                      <MenuItem value={DeviceType.BLE_GATEWAY}>{t('ble_gateway')}</MenuItem>
                      <MenuItem value={DeviceType.FIXED_RFID_READER}>{t('fixed_rfid_reader')}</MenuItem>
                    </Select>
                    <FormHelperText>{errors.type?.message} </FormHelperText>
                  </FormControl>
                )}
              />

              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField error={errors.name !== undefined} helperText={errors.name?.message} label={t('name')} {...field} />
                )}
              />

              {(watch('deviceType') === DeviceType.BLE_GATEWAY || watch('deviceType') === DeviceType.FIXED_RFID_READER) && (
                <>
                  <Controller
                    name="identifier"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        error={errors.identifier !== undefined}
                        helperText={errors.identifier?.message}
                        label={t('identifier')}
                        {...field}
                      />
                    )}
                  />
                </>
              )}

              {watch('deviceType') === DeviceType.MOBILE_SCANNER && (
                <Controller
                  name="code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      error={errors.code !== undefined}
                      helperText={errors.code?.message ?? t('code_can_be_used_in_posito_scan_app')}
                      label={t('code')}
                      {...field}
                    />
                  )}
                />
              )}

              <Controller
                name="brand"
                control={control}
                render={({ field }) => (
                  <TextField error={errors.brand !== undefined} helperText={errors.brand?.message} label={t('brand')} {...field} />
                )}
              />

              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <TextField error={errors.type !== undefined} helperText={errors.type?.message} label={t('description')} {...field} />
                )}
              />

              <Controller
                name="serialNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.serialNumber !== undefined}
                    helperText={errors.serialNumber?.message}
                    label={t('serial_number')}
                    {...field}
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>
        {(watch('deviceType') === DeviceType.BLE_GATEWAY || watch('deviceType') === DeviceType.FIXED_RFID_READER) && (
          <>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" className="pb-2" color="textSecondary">
                  {t('configuration')}
                </Typography>
                <div className="flex w-full flex-col gap-y-2">
                  <Controller
                    name="locationId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <LocationSelect
                        helperText={errors.locationId?.message}
                        label={t('device') + ' ' + t('location')}
                        selectedLocationId={value !== null ? value : undefined}
                        onSelected={(selectedLocation) => {
                          if (selectedLocation) {
                            onChange(selectedLocation.dto.id);
                          } else {
                            onChange(null);
                          }
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="moveTagsToLocationTimeMinutes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        error={errors.moveTagsToLocationTimeMinutes !== undefined}
                        helperText={errors.moveTagsToLocationTimeMinutes?.message}
                        label={t('move_tags_to_location_time_minutes')}
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                      />
                    )}
                  />

                  <Controller
                    name="moveTagsLocationId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <LocationSelect
                        helperText={errors.moveTagsLocationId?.message}
                        label={t('move_tags_location')}
                        selectedLocationId={value !== null ? value : undefined}
                        onSelected={(selectedLocation) => {
                          if (selectedLocation) {
                            onChange(selectedLocation.dto.id);
                          } else {
                            onChange(null);
                          }
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="maxTimeNotSeenMinutes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        error={errors.maxTimeNotSeenMinutes !== undefined}
                        helperText={errors.maxTimeNotSeenMinutes?.message}
                        label={t('max_time_not_seen_minutes')}
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                      />
                    )}
                  />

                  <Controller
                    name="alertEmails"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                          {t('alert_emails')}
                        </Typography>

                        {/* Input field for new email */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                          <TextField
                            fullWidth
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder={t('enter_email_address')}
                            error={errors.alertEmails !== undefined}
                            helperText={errors.alertEmails?.message}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (newEmail.trim() && newEmail.includes('@')) {
                                  const currentEmails = value || [];
                                  if (!currentEmails.includes(newEmail.trim())) {
                                    onChange([...currentEmails, newEmail.trim()]);
                                    setNewEmail('');
                                  }
                                }
                              }
                            }}
                          />
                          <Button
                            variant="outlined"
                            className="ml-2 min-w-3"
                            onClick={() => {
                              if (newEmail.trim() && newEmail.includes('@')) {
                                const currentEmails = value || [];
                                if (!currentEmails.includes(newEmail.trim())) {
                                  onChange([...currentEmails, newEmail.trim()]);
                                  setNewEmail('');
                                }
                              }
                            }}
                            disabled={!newEmail.trim() || !newEmail.includes('@')}
                          >
                            {<Add />}
                          </Button>
                        </Box>

                        {/* Display existing emails as chips */}
                        {value && value.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {value.map((email, index) => (
                              <Chip
                                key={index}
                                label={email}
                                onDelete={() => {
                                  const updatedEmails = value.filter((_, i) => i !== index);
                                  onChange(updatedEmails.length > 0 ? updatedEmails : undefined);
                                }}
                                deleteIcon={<Delete />}
                                variant="outlined"
                                size="small"
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <ActionBar visible={hasDeviceEditPermission}>
        <Button variant="contained" type="submit" loading={isSubmitting}>
          {t('save')}
        </Button>
      </ActionBar>
    </form>
  );
};

export default DeviceFormDisplay;
